import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext';

/**
 * SignInPage Component
 *
 * Handles usernameless passkey-based authentication for returning users.
 * No email required - just click and authenticate with biometrics!
 * New users should use the /activate route to set up their account.
 */
export function SignInPage() {
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { signIn } = useAuth();
  const navigate = useNavigate();

  async function handlePasskeySignIn(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Usernameless passkey authentication flow
      // This will trigger the browser's WebAuthn prompt with all available passkeys
      const result = await signIn();

      if (result.isSignedIn) {
        navigate('/dashboard');
      } else {
        setError(
          result.error || 'Failed to sign in with passkey. Please try again.'
        );
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to sign in with passkey';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-cream">
      <div className="w-full max-w-md">
        {/* Logo/Brand */}
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">🔐</div>
          <h1 className="text-4xl font-bold mb-2 text-text-dark">
            Metabolic Health Coach
          </h1>
          <p className="text-lg text-text-secondary">
            Sign in with your passkey
          </p>
        </div>

        {/* Sign-in card */}
        <div className="card">
          <h2 className="text-2xl font-semibold mb-6 text-text-dark">
            Sign In
          </h2>

          {error && (
            <div className="mb-4 p-3 rounded-lg text-sm bg-red-50 text-primary border border-primary">
              {error}
            </div>
          )}

          <form onSubmit={handlePasskeySignIn}>
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Authenticating...' : 'Sign In with Passkey'}
            </button>
          </form>

          <div className="mt-6 space-y-4">
            <div className="text-center text-sm text-text-secondary">
              <p>
                Your passkey will be used to securely sign you in. No password
                required!
              </p>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-text-secondary">
                  New user?
                </span>
              </div>
            </div>

            <button
              onClick={() => navigate('/activate')}
              className="w-full px-6 py-3 rounded-full font-semibold text-text-dark bg-beige hover:bg-opacity-80 transition-colors"
            >
              Activate Your Account
            </button>

            <p className="text-xs text-center text-text-secondary">
              Don't have an activation code? Contact your administrator.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-text-secondary">
          <p>&copy; 2025 Metabolic Health Coach. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
